import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Box } from '@wix/design-system';
import {
  Chat,
  Email,
  Mobile,
  Phone,
  StatusAlertSmall,
} from '@wix/wix-ui-icons-common';
import { DialogFormContainer } from '../DialogContainer';
import { BodyContainer } from '../AuthPage/AuthPage';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import { ThemedText } from '../ThemedComponents';
import { AppContextProvider } from '../AppLoader';
import { EnterCodeContextProvider } from '../EnterCode/EnterCode';
import { useGenericEventsLogger } from '@wix/data-tools-generic-events';
import { useTwoFactorAuth } from '../../stores/twoFactorAuth';
import { extractErrorKeyByErrorCode } from '../../utils/errorHandler';
import { dataHooks } from '../../dataHooks';
import AuthenticatorIcon from '../../assets/images/authenticator.svg';
import WhatsappIcon from '../../assets/images/whatsapp.svg';
import s from './TwoFaLobby.scss';

const twoFaLobbyMethodIcons: Record<string, JSX.Element> = {
  totp: <AuthenticatorIcon />,
  push: <Mobile />,
  phone_whatsapp: <WhatsappIcon />,
  phone: <Chat />,
  phone_call: <Phone />,
  email: <Email />,
};

export const TwoFaLobby: React.FC = observer(() => {
  const { t } = useTranslation();
  const genericEventsLogger = useGenericEventsLogger();
  const { store } = useTwoFactorAuth();
  const { enterCodeStore } = useContext(EnterCodeContextProvider);
  const [switchError, setSwitchError] = useState<{
    key: string;
    message: string;
  } | null>(null);
  const {
    rootStore: {
      displayStore: { isWixel },
    },
  } = useContext(AppContextProvider);

  return (
    <Box direction="vertical" width="100%" align="center">
      <DialogFormContainer
        dataHook={dataHooks.twoFaLobby.container}
        formClasses={[s.form]}
        mainHeaderProps={{
          showBackButton: true,
          backButtonCallback: () =>
            enterCodeStore?.onBackButtonClicked(store.currentTwoFAMethod),
        }}
      >
        <BodyContainer priority="secondary">
          <DialogHeader maxWidth="380px">
            <DialogHeader.Title as="h1" flow="secondaryFlow">
              {t('enter_code.lobby.title')}
            </DialogHeader.Title>
            <DialogHeader.SubTitleSmall>
              {t('enter_code.lobby.description')}
            </DialogHeader.SubTitleSmall>
          </DialogHeader>
          <Box
            direction="vertical"
            align="center"
            width="100%"
            gap={3}
            className={s.methods}
          >
            {store.twoFaLobbyMethods.map((method) => (
              <Box
                key={method.key}
                direction="vertical"
                width="100%"
                align="left"
                gap={1}
              >
                <ButtonWithLoader
                  dataHook={dataHooks.twoFaLobby.method(method.key)}
                  priority="secondary"
                  {...(isWixel ? { skin: 'standard' as const } : {})}
                  prefixIcon={twoFaLobbyMethodIcons[method.key]}
                  hidePrefixIconWhileLoading
                  ellipsis
                  className={classNames(s.methodButton, {
                    [s.wixel]: isWixel,
                    [s.a11yFocus]: isWixel,
                  })}
                  onClick={async () => {
                    setSwitchError(null);
                    genericEventsLogger.clickCta({
                      componentName: `two-fa-lobby-${method.key.replace(
                        /_/g,
                        '-',
                      )}`,
                    });
                    const errorCode = await enterCodeStore?.onSwitchMethod(
                      method.method,
                      store,
                      {
                        phoneDeliveryMethod: method.phoneDeliveryMethod,
                        suppressToast: true,
                      },
                    );
                    if (errorCode) {
                      setSwitchError({
                        key: method.key,
                        message: t(extractErrorKeyByErrorCode(errorCode)),
                      });
                    }
                  }}
                >
                  {t(method.labelKey)}
                </ButtonWithLoader>
                {switchError?.key === method.key && (
                  <ThemedText
                    skin="error"
                    size="small"
                    weight="normal"
                    className={s.errorText}
                    dataHook={dataHooks.twoFaLobby.methodError(method.key)}
                  >
                    <StatusAlertSmall />
                    {switchError.message}
                  </ThemedText>
                )}
              </Box>
            ))}
          </Box>
        </BodyContainer>
      </DialogFormContainer>
    </Box>
  );
});

export default TwoFaLobby;
